import { Injectable } from "@angular/core";

import { NavigationService, ToastService } from "@tylertech/core-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { MetadataActionService } from "@tylertech/metadata-module";
import
{
    ActionType,
    ExecutionResult,
    IAggregate,
    ICardAction,
    ICardData,
    ICardDataElement,
    PerformActionParams,
} from "@tylertech/tyler-api-types";

import { AggregateService } from "./aggregateService.g";

@Injectable()
export class AggregateHelperService
{
    constructor(
        private readonly $toastService: ToastService,
        private readonly $navigationService: NavigationService,
        private readonly $commonDialogsService: CommonDialogsService,
        private readonly $metadataActionService: MetadataActionService,
        private readonly $aggregateService: AggregateService,
    ) { }

    getCount(agg: IAggregate): number
    {
        const subAggregates = agg.SubAggregates;
        if (subAggregates && subAggregates.length && subAggregates.length > 0)
        {
            let count = 0;
            for (let i = 0; i < subAggregates.length; i++)
            {
                const subAgg = subAggregates[i];
                count += this.getCount(subAgg);
            }
            return count;
        }
        return agg.Count !== 0 ? agg.Count : 0;
    }

    getDataElementByLabel(fieldName: string, cardDataElements: ICardDataElement[]): ICardDataElement | undefined
    {
        return cardDataElements?.find(x => x.Label === fieldName);
    }

    getFieldValue(cardData: ICardData, fieldName: string): string | undefined
    {
        return this.getDataElementByLabel(fieldName, cardData.DataElements)?.Value;
    }

    isHidden(aggregate: IAggregate): boolean
    {
        if (!aggregate)
        {
            return false;
        }

        const isChartOnly = this.getDataElementByLabel("ChartOnly", aggregate.AggregateCardDataElements);
        if (!isChartOnly?.Value)
        {
            return false;
        }
        else
        {
            return isChartOnly.Value === "true";
        }
    }

    performServerCardAction(aggregate: IAggregate, cardData: ICardData, action: ICardAction): Promise<ICardData>
    {
        const self = this;
        const model: PerformActionParams = {
            Aggregate: aggregate,
            Card: cardData,
            CardAction: action,
            UserId: "",
            Action: undefined,
            AggregateProviderId: undefined,
            CardProviderId: undefined,
            ConfirmationQuestions: undefined,
        };

        const metadataVerificationCallback: () => Promise<unknown> = () =>
        {
            return this.$aggregateService.PerformCardAction(model)
                .then(onSuccessToast);
        };

        return this.$aggregateService.PerformCardAction(model)
            .then(onSuccessToast)
            .then(
                (result?: ICardData) =>
                {
                    if (action.actionType !== ActionType.ServerUrl)
                    {
                        return result;
                    }

                    const url = this.getDataElementByLabel("Url", result.DataElements);
                    if (!url.Value)
                    {
                        return result;
                    }

                    if (url.Value.substring(0, 1) === "/")
                    {
                        this.$navigationService.navigateToApp(url.Value);
                    }
                    else
                    {
                        window.location.href = url.Value;
                    }
                },
                this.$metadataActionService.GetVerificationHandler(model, metadataVerificationCallback, model));

        function onSuccessToast(result: ExecutionResult<ICardData>): ICardData | Promise<ICardData>
        {
            if (result.Success)
            {
                if (result.ValidationMessage)
                {
                    self.$toastService.showToast(result.ValidationMessage);
                }
                return result.Result;
            }

            self.$commonDialogsService.showAlertHtmlMessage(
                !result.ErrorMessage
                    ? result.ValidationMessage
                    : result.ErrorMessage,
                "Error Performing Action");
            return Promise.reject();
        }
    }

}
